import React, { useState } from 'react';
import { SinglePatch, GroupPatch } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';
import { PATCH_SIGN, PATCH_BADDIE, PATCH_LETTER_A, GROUP_PATCHES } from './patchDemoData';

/**
 * SinglePatch and GroupPatch share one page: they are the two chips of the
 * "placed patches" row and sit side by side there, so they are judged together.
 */

export const meta = {
  title: 'Patches',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '4275:22815',
  description:
    'Chips for patches already placed on the garment. SinglePatch is one 36px tile, GroupPatch a set of patches placed together (124×36 for three). Selecting either reveals a black remove button centred on its top-right corner. Both are 36px tall so they line up in one row; `size="large"` is the OVER/LL "Min patchar" tray size: 52px from 1024px up, 36px below.',
  propSets: [
    {
      title: 'SinglePatch',
      component: SinglePatch,
      componentName: 'SinglePatch',
      rows: [
        ['image', 'string', '—', 'Patch image — contained with a 12.5% inset'],
        ['imageAlt', 'string', "''", 'Alt text — also the tile button\'s accessible name'],
        ['selected', 'bool', 'false', 'Selected state — white tile, gray-10 border, remove button'],
        ['size', "'default' | 'large'", "'default'", 'Tile size — 36px, or large: 52px from 1024px up'],
        ['onClick', 'func', '—', 'Tile click handler'],
        ['onRemove', 'func', '—', 'Shows the remove button while selected'],
        ['removeLabel', 'string', "'Remove'", 'Accessible name of the remove button'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
    {
      title: 'GroupPatch',
      component: GroupPatch,
      componentName: 'GroupPatch',
      rows: [
        ['patches', '[{id, image, imageAlt}]', '[]', 'Patches in the group, left to right'],
        ['label', 'string', '—', 'Accessible name for the whole group (replaces the per-image alts)'],
        ['selected', 'bool', 'false', 'Selected state — white fill, gray-10 border, remove button'],
        ['selectedIndex', 'number', 'null', 'Selected patch (with onPatchClick) — white slot, gray-10 stroke, remove button on the group'],
        ['size', "'default' | 'large'", "'default'", 'Slot size — 36px, or large: 52px from 1024px up'],
        ['onClick', 'func', '—', 'Group click handler (whole-group selection)'],
        ['onPatchClick', 'func', '—', 'Patch click handler (gets the index) — every patch becomes its own button'],
        ['onRemove', 'func', '—', 'Removes the whole group; shows while the group or one of its patches is selected'],
        ['removeLabel', 'string', "'Remove'", 'Accessible name of the remove button'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
  ],
  usage: [
    {
      title: 'Placed patches row',
      code: `import { SinglePatch, GroupPatch } from '@ajat/design-system/Configurator';

<div className="flex flex-wrap items-center gap-medium-s">
  {placed.map((item) => item.patches ? (
    <GroupPatch
      key={item.id}
      patches={item.patches}
      label={item.name}
      selected={item.id === selectedId}
      onClick={() => setSelectedId(item.id)}
      onRemove={() => removePlaced(item.id)}
    />
  ) : (
    <SinglePatch
      key={item.id}
      image={item.image}
      imageAlt={item.name}
      selected={item.id === selectedId}
      onClick={() => setSelectedId(item.id)}
      onRemove={() => removePlaced(item.id)}
    />
  ))}
</div>`,
    },
  ],
};

const INITIAL_PLACED = [
  { id: 'sign', image: PATCH_SIGN, name: 'Sign' },
  { id: 'set', patches: GROUP_PATCHES, name: 'Butterfly, heart and star' },
  { id: 'baddie', image: PATCH_BADDIE, name: 'Baddie' },
  { id: 'letter', image: PATCH_LETTER_A, name: 'Letter A' },
];

export default function PatchesPage() {
  const [placed, setPlaced] = useState(INITIAL_PLACED);
  const [selectedId, setSelectedId] = useState('set');

  const remove = (id) => {
    setPlaced((items) => items.filter((item) => item.id !== id));
    setSelectedId(null);
  };

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="SinglePatch states" hint="hover for the Hover state">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><SinglePatch image={PATCH_SIGN} imageAlt="Sign" /></Demo>
          <Demo caption="SELECTED" center><SinglePatch image={PATCH_SIGN} imageAlt="Sign" selected onRemove={() => {}} /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="GroupPatch states" hint="hover for the Hover state">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><GroupPatch patches={GROUP_PATCHES} label="Butterfly, heart and star" /></Demo>
          <Demo caption="SELECTED" center><GroupPatch patches={GROUP_PATCHES} label="Butterfly, heart and star" selected onRemove={() => {}} /></Demo>
          <Demo caption="PATCH SELECTED" center><GroupPatch patches={GROUP_PATCHES} label="Butterfly, heart and star" selectedIndex={1} onPatchClick={() => {}} onRemove={() => {}} /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Large" hint="OVER/LL tray — 52px from 1024px, 36px below">
        <DemoRow align="center">
          <Demo caption="SINGLE" center><SinglePatch size="large" image={PATCH_SIGN} imageAlt="Sign" /></Demo>
          <Demo caption="GROUP" center><GroupPatch size="large" patches={GROUP_PATCHES} label="Butterfly, heart and star" /></Demo>
          <Demo caption="SELECTED" center><SinglePatch size="large" image={PATCH_BADDIE} imageAlt="Baddie" selected onRemove={() => {}} /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Placed patches row" hint="select a chip, then remove it">
        <div className="flex flex-wrap items-center gap-medium-s pt-small-l">
          {placed.map((item) => (item.patches ? (
            <GroupPatch
              key={item.id}
              patches={item.patches}
              label={item.name}
              selected={item.id === selectedId}
              onClick={() => setSelectedId(item.id)}
              onRemove={() => remove(item.id)}
            />
          ) : (
            <SinglePatch
              key={item.id}
              image={item.image}
              imageAlt={item.name}
              selected={item.id === selectedId}
              onClick={() => setSelectedId(item.id)}
              onRemove={() => remove(item.id)}
            />
          )))}
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
